/**
 * Client-side PDF generation for single post download.
 */

import html2pdf from 'html2pdf.js';

const PDF_SANDBOX_CLASS = 'alteia-pdf-sandbox';
const PDF_EXPORT_CLASS = 'alteia-single-post--pdf-export';

/** A4 page size in px at 96 dpi. */
const PDF_A4_WIDTH_PX = Math.round( ( 210 * 96 ) / 25.4 );
const PDF_A4_HEIGHT_PX = Math.round( ( 297 * 96 ) / 25.4 );

/** PDF vertical margin in mm (top and bottom). */
const PDF_MARGIN_VERTICAL_MM = 12;

/** PDF horizontal margin in mm (left and right) — wider than vertical for a narrower column. */
const PDF_MARGIN_HORIZONTAL_MM = 18;

/** PDF margins in px at 96 dpi — keeps jsPDF page math aligned with DOM rects. */
const PDF_MARGIN_TOP_PX = Math.round( ( PDF_MARGIN_VERTICAL_MM * 96 ) / 25.4 );
const PDF_MARGIN_BOTTOM_PX = PDF_MARGIN_TOP_PX;
const PDF_MARGIN_LEFT_PX = Math.round(
	( PDF_MARGIN_HORIZONTAL_MM * 96 ) / 25.4
);
const PDF_MARGIN_RIGHT_PX = PDF_MARGIN_LEFT_PX;

/** A4 printable content width in px — matches inner page width to avoid upscale zoom. */
const PDF_CONTENT_WIDTH_PX =
	PDF_A4_WIDTH_PX - PDF_MARGIN_LEFT_PX - PDF_MARGIN_RIGHT_PX;

/**
 * Extra px before a page edge so line glyphs are not sliced when html2pdf
 * inserts break spacers from getBoundingClientRect (known off-by-a-few-px issue).
 */
const PDF_PAGE_EDGE_BUFFER_PX = 14;

/** @type {string} */
const PDF_BREAK_UNIT_SELECTOR = [
	'.alteia-single-post__print-meta',
	'.alteia-single-post__title',
	'.alteia-single-post__featured-image',
	'.alteia-pdf-heading-group',
	'.alteia-pdf-text-block',
	'.alteia-pdf-media-block',
	'.alteia-pdf-quote-block',
	'.alteia-article-disclaimer',
].join( ', ' );

/** @type {Promise<void>} */
let downloadQueue = Promise.resolve();

/**
 * Build a filesystem-safe PDF filename from the post title.
 *
 * @param {string} title Post title.
 * @return {string} Filename ending in .pdf
 */
export function sanitizePdfFilename( title ) {
	const cleaned = ( title || 'post' )
		.replace( /[<>:"/\\|?*\u0000-\u001F]/g, '' )
		.replace( /\s+/g, ' ' )
		.trim();

	return `${ cleaned || 'post' }.pdf`;
}

/**
 * Resolve the post title for the PDF filename.
 *
 * @param {HTMLElement}      root        Article root element.
 * @param {string|undefined} buttonTitle Title from the download button.
 * @return {string} Sanitized post title for the PDF filename.
 */
export function resolvePostTitle( root, buttonTitle ) {
	const fromButton = ( buttonTitle || '' ).trim();
	if ( fromButton ) {
		return fromButton;
	}

	const fromHeading = root
		.querySelector( '.alteia-single-post__title' )
		?.textContent?.trim();

	if ( fromHeading ) {
		return fromHeading;
	}

	return document.title.split( '|' )[ 0 ].trim() || 'post';
}

/**
 * Remove leftover html2pdf DOM nodes and release large canvases.
 */
function cleanupHtml2PdfArtifacts() {
	document
		.querySelectorAll( '.html2pdf__overlay, .html2pdf__container' )
		.forEach( ( element ) => {
			element.querySelectorAll( 'canvas' ).forEach( ( canvas ) => {
				canvas.width = 0;
				canvas.height = 0;
			} );
			element.remove();
		} );
}

/**
 * Reset any stuck sandbox or html2pdf nodes from a prior failed download.
 */
function resetStuckCaptureState() {
	document
		.querySelectorAll( `.${ PDF_SANDBOX_CLASS }` )
		.forEach( ( element ) => element.remove() );
	cleanupHtml2PdfArtifacts();
}

/**
 * Trigger a file download from a Blob.
 *
 * @param {Blob}   blob     PDF blob.
 * @param {string} filename Download filename.
 * @return {Promise<void>}
 */
function triggerBlobDownload( blob, filename ) {
	return new Promise( ( resolve, reject ) => {
		const url = URL.createObjectURL( blob );
		const link = document.createElement( 'a' );
		link.href = url;
		link.download = filename;
		link.rel = 'noopener';
		link.style.display = 'none';
		document.body.appendChild( link );

		try {
			link.click();
			resolve();
		} catch ( error ) {
			reject( error );
		} finally {
			document.body.removeChild( link );
			window.setTimeout( () => URL.revokeObjectURL( url ), 1000 );
		}
	} );
}

/** @type {string} */
const PDF_QUOTE_SELECTOR =
	'blockquote, .wp-block-quote, figure.wp-block-pullquote';

/** @type {string} */
const PDF_AVOID_BREAK_SELECTOR = [
	'p',
	'h2',
	'h3',
	'h4',
	'h5',
	'h6',
	'li',
	'hr',
	'figure',
	'.wp-block-image',
	'.wp-block-gallery',
	'.wp-block-group',
	'.wp-block-columns',
	'.wp-block-column',
	'.wp-block-media-text',
	'.wp-block-cover',
	'.wp-block-embed',
	'.wp-block-separator',
	'.alteia-pdf-text-block',
	'.alteia-pdf-heading-group',
	'.alteia-pdf-media-block',
	'.alteia-pdf-quote-block',
	'.alteia-single-post__featured-image',
	'blockquote',
	'.wp-block-quote',
	'figure.wp-block-pullquote',
	'table',
	'.wp-block-table',
].join( ', ' );

/** @type {string[]} */
const PDF_PAGE_BREAK_AVOID = [
	'.alteia-pdf-avoid-break',
	'.alteia-pdf-text-block',
	'.alteia-pdf-heading-group',
	'.alteia-pdf-media-block',
	'.alteia-pdf-quote-block',
	'p',
	'h2',
	'h3',
	'h4',
	'h5',
	'h6',
	'li',
	'hr',
	'figure',
	'.wp-block-image',
	'.wp-block-gallery',
	'.wp-block-group',
	'.wp-block-columns',
	'.wp-block-column',
	'.wp-block-media-text',
	'.wp-block-cover',
	'.wp-block-embed',
	'.wp-block-separator',
	'blockquote',
	'.wp-block-quote',
	'figure.wp-block-pullquote',
	'table',
	'.wp-block-table',
	'.alteia-single-post__featured-image',
];

/**
 * Wrap standalone body images so html2pdf can keep each image on one page.
 *
 * @param {HTMLElement} root Article root element.
 * @return {void}
 */
function wrapLooseBodyImages( root ) {
	root.querySelectorAll( '.alteia-single-post__body img' ).forEach(
		( img ) => {
			if (
				img.closest(
					'figure, .wp-block-image, .wp-block-gallery, .alteia-pdf-media-block'
				)
			) {
				return;
			}

			const parent = img.parentElement;

			if ( ! parent ) {
				return;
			}

			const wrap = img.ownerDocument.createElement( 'div' );
			wrap.className = 'alteia-pdf-media-block alteia-pdf-avoid-break';
			parent.insertBefore( wrap, img );
			wrap.appendChild( img );
		}
	);
}

/**
 * Wrap quote blocks so html2pdf keeps each quote on a single page.
 *
 * @param {HTMLElement} root Article root element.
 * @return {void}
 */
function wrapQuoteBlocks( root ) {
	const body = root.querySelector( '.alteia-single-post__body' );

	if ( ! body ) {
		return;
	}

	body.querySelectorAll( PDF_QUOTE_SELECTOR ).forEach( ( quote ) => {
		if ( quote.closest( '.alteia-pdf-quote-block' ) ) {
			return;
		}

		if (
			quote.matches( 'blockquote' ) &&
			quote.closest( 'figure.wp-block-pullquote' )
		) {
			return;
		}

		const parent = quote.parentElement;

		if ( ! parent ) {
			return;
		}

		const wrap = quote.ownerDocument.createElement( 'div' );
		wrap.className = 'alteia-pdf-quote-block alteia-pdf-avoid-break';
		parent.insertBefore( wrap, quote );
		wrap.appendChild( quote );
	} );
}

/**
 * Mark content blocks that must not be split across PDF pages.
 *
 * @param {HTMLElement} root Article root element.
 * @return {void}
 */
function markPageBreakTargets( root ) {
	root.querySelectorAll( PDF_AVOID_BREAK_SELECTOR ).forEach( ( element ) => {
		element.classList.add( 'alteia-pdf-avoid-break' );
	} );
}

/**
 * Mark h2 section headings that need a top border in PDF export.
 * Wrapper divs break :first-of-type, so borders are applied via class.
 *
 * @param {HTMLElement} root Article root element.
 * @return {void}
 */
function markSectionHeadingBorders( root ) {
	const body = root.querySelector( '.alteia-single-post__body' );

	if ( ! body ) {
		return;
	}

	let seenH2 = false;

	body.querySelectorAll( 'h2' ).forEach( ( heading ) => {
		if ( seenH2 ) {
			heading.classList.add( 'alteia-pdf-heading--section-start' );
		}

		seenH2 = true;
	} );
}

/**
 * Wrap each standalone paragraph so html2pdf treats it as one break unit.
 *
 * @param {HTMLElement} root Article root element.
 * @return {void}
 */
function wrapTextParagraphs( root ) {
	const body = root.querySelector( '.alteia-single-post__body' );

	if ( ! body ) {
		return;
	}

	body.querySelectorAll( 'p' ).forEach( ( paragraph ) => {
		if (
			paragraph.closest(
				'.alteia-pdf-text-block, blockquote, .wp-block-quote, figure, td, th, .alteia-pdf-quote-block, .alteia-pdf-heading-group'
			)
		) {
			return;
		}

		const parent = paragraph.parentElement;

		if ( ! parent ) {
			return;
		}

		const wrap = paragraph.ownerDocument.createElement( 'div' );
		wrap.className = 'alteia-pdf-text-block alteia-pdf-avoid-break';
		parent.insertBefore( wrap, paragraph );
		wrap.appendChild( paragraph );
	} );
}

/**
 * Keep each heading with its immediate following block on the same page.
 *
 * @param {HTMLElement} root Article root element.
 * @return {void}
 */
function wrapHeadingGroups( root ) {
	const body = root.querySelector( '.alteia-single-post__body' );

	if ( ! body ) {
		return;
	}

	body.querySelectorAll( 'h2, h3, h4, h5, h6' ).forEach( ( heading ) => {
		if ( heading.closest( '.alteia-pdf-heading-group' ) ) {
			return;
		}

		const next = heading.nextElementSibling;

		if (
			! next ||
			next.matches( 'h2, h3, h4, h5, h6' ) ||
			next.closest( '.alteia-pdf-heading-group' )
		) {
			return;
		}

		const parent = heading.parentElement;

		if ( ! parent ) {
			return;
		}

		const group = heading.ownerDocument.createElement( 'div' );
		group.className = 'alteia-pdf-heading-group alteia-pdf-avoid-break';
		parent.insertBefore( group, heading );
		group.appendChild( heading );
		group.appendChild( next );
	} );
}

/**
 * Inner printable page height in px (A4 minus margins).
 *
 * @return {number} Printable page height in pixels.
 */
function getPdfPageInnerHeightPx() {
	return PDF_A4_HEIGHT_PX - PDF_MARGIN_TOP_PX - PDF_MARGIN_BOTTOM_PX;
}

/**
 * Ensure export capture nodes do not clip text at box edges.
 *
 * @param {HTMLElement} root Article root element.
 * @return {void}
 */
function applyPdfOverflowVisible( root ) {
	const targets = [
		root,
		...root.querySelectorAll(
			'.alteia-single-post__layout, .alteia-single-post__content, .alteia-single-post__body, .alteia-pdf-text-block, .alteia-pdf-heading-group, .alteia-pdf-media-block, .alteia-pdf-quote-block, .alteia-single-post__title, .alteia-single-post__print-meta, .alteia-article-disclaimer'
		),
	];

	targets.forEach( ( node ) => {
		if ( node instanceof HTMLElement ) {
			node.style.overflow = 'visible';
		}
	} );
}

/**
 * Insert a spacer before an element to push it below the current page break.
 *
 * @param {HTMLElement} beforeElement Element to push to the next page.
 * @param {number}      heightPx      Spacer height in px.
 * @return {void}
 */
function insertPageSpacer( beforeElement, heightPx ) {
	if (
		beforeElement.previousElementSibling?.classList.contains(
			'alteia-pdf-page-spacer'
		)
	) {
		return;
	}

	const spacer = beforeElement.ownerDocument.createElement( 'div' );
	spacer.className = 'alteia-pdf-page-spacer';
	spacer.setAttribute( 'aria-hidden', 'true' );
	spacer.style.cssText = `display:block;height:${ heightPx }px;margin:0;padding:0;border:0;overflow:visible;`;
	beforeElement.parentNode.insertBefore( spacer, beforeElement );
}

/**
 * Insert page spacers before blocks that would straddle a PDF page boundary.
 * Re-measures after each insert because earlier spacers shift layout downward.
 *
 * @param {HTMLElement} root Article root element.
 * @return {void}
 */
function injectPdfPageBreaks( root ) {
	const pageHeight = getPdfPageInnerHeightPx();
	const maxPasses = 400;

	for ( let pass = 0; pass < maxPasses; pass++ ) {
		const rootTop = root.getBoundingClientRect().top;
		let inserted = false;

		const breakUnits = root.querySelectorAll( PDF_BREAK_UNIT_SELECTOR );

		for ( const element of breakUnits ) {
			if (
				element.previousElementSibling?.classList.contains(
					'alteia-pdf-page-spacer'
				)
			) {
				continue;
			}

			const rect = element.getBoundingClientRect();
			const blockHeight = rect.height;

			if ( blockHeight <= 0 || blockHeight > pageHeight ) {
				continue;
			}

			const topOffset = rect.top - rootTop;
			const bottomOffset = rect.bottom - rootTop;
			const startPage = Math.floor( topOffset / pageHeight );
			const endPage = Math.floor(
				( bottomOffset - PDF_PAGE_EDGE_BUFFER_PX ) / pageHeight
			);

			if ( endPage > startPage ) {
				const pageEnd = ( startPage + 1 ) * pageHeight + rootTop;
				const spacerHeight =
					pageEnd - rect.top + PDF_PAGE_EDGE_BUFFER_PX;
				insertPageSpacer( element, spacerHeight );
				inserted = true;
				break;
			}
		}

		if ( ! inserted ) {
			break;
		}
	}
}

/**
 * Prepare page-break targets for PDF export.
 *
 * @param {HTMLElement} root Article root element.
 * @return {void}
 */
function preparePageBreakTargets( root ) {
	markSectionHeadingBorders( root );
	wrapHeadingGroups( root );
	wrapTextParagraphs( root );
	wrapLooseBodyImages( root );
	wrapQuoteBlocks( root );
	markPageBreakTargets( root );
	applyPdfOverflowVisible( root );
}

/**
 * Final PDF layout pass after fonts/images are ready.
 *
 * @param {HTMLElement} root Article root element.
 * @return {void}
 */
function finalizePdfPageLayout( root ) {
	applyPdfOverflowVisible( root );
	injectPdfPageBreaks( root );
}

/**
 * Clone and prepare article markup for PDF export (print layout, no sidebar).
 *
 * @param {HTMLElement} root Live article root element.
 * @return {HTMLElement} Prepared clone element.
 */
function preparePdfExportClone( root ) {
	const clone = /** @type {HTMLElement} */ ( root.cloneNode( true ) );

	clone.classList.add( PDF_EXPORT_CLASS );

	clone
		.querySelectorAll( '.alteia-single-post__print-meta' )
		.forEach( ( element ) => {
			element.removeAttribute( 'aria-hidden' );
		} );

	clone
		.querySelectorAll(
			'.alteia-single-post__sidebar-column, .alteia-single-post__back-wrap, .alteia-single-post__top-meta'
		)
		.forEach( ( element ) => {
			element.remove();
		} );

	clone
		.querySelectorAll( 'button, script, noscript, iframe, video' )
		.forEach( ( element ) => {
			element.remove();
		} );

	preparePageBreakTargets( clone );

	return clone;
}

/**
 * Build an off-screen article clone for PDF capture.
 * The live page DOM is never modified — only the sandbox is styled for export.
 *
 * @param {HTMLElement} root Live article root element.
 * @return {{ sandbox: HTMLDivElement, clone: HTMLElement }} Sandbox wrapper and clone.
 */
function buildPdfCaptureSandbox( root ) {
	const clone = preparePdfExportClone( root );

	const sandbox = document.createElement( 'div' );
	sandbox.className = PDF_SANDBOX_CLASS;
	sandbox.setAttribute( 'aria-hidden', 'true' );
	sandbox.style.width = `${ PDF_CONTENT_WIDTH_PX }px`;

	sandbox.appendChild( clone );
	document.body.appendChild( sandbox );

	return { sandbox, clone };
}

/**
 * Wait for layout, fonts, and images before html2canvas capture.
 *
 * @param {HTMLElement} root Article root element.
 * @return {Promise<void>}
 */
async function waitForCaptureReady( root ) {
	if ( document.fonts?.ready ) {
		await document.fonts.ready;
	}

	const images = Array.from( root.querySelectorAll( 'img' ) );
	await Promise.all(
		images.map(
			( img ) =>
				new Promise( ( resolve ) => {
					if ( img.complete ) {
						resolve();
						return;
					}
					img.addEventListener( 'load', resolve, { once: true } );
					img.addEventListener( 'error', resolve, { once: true } );
				} )
		)
	);

	await new Promise( ( resolve ) => {
		window.requestAnimationFrame( () => {
			window.requestAnimationFrame( resolve );
		} );
	} );
}

/**
 * Brief pause so the browser can release canvas memory between runs.
 *
 * @return {Promise<void>}
 */
function waitForMemoryRelease() {
	return new Promise( ( resolve ) => {
		window.setTimeout( resolve, 400 );
	} );
}

/**
 * Run a single PDF download job.
 *
 * @param {HTMLElement}                                 root         Single post root element.
 * @param {string}                                      postTitle    Post title for filename.
 * @param {(state: 'generating'|'downloading') => void} [onProgress] Progress callback.
 * @return {Promise<void>}
 */
async function runDownloadJob( root, postTitle, onProgress ) {
	resetStuckCaptureState();

	const filename = sanitizePdfFilename( resolvePostTitle( root, postTitle ) );

	if ( onProgress ) {
		onProgress( 'generating' );
	}

	const { sandbox, clone } = buildPdfCaptureSandbox( root );

	try {
		await waitForCaptureReady( clone );
		finalizePdfPageLayout( clone );

		if ( onProgress ) {
			onProgress( 'downloading' );
		}

		const worker = html2pdf().set( {
			margin: [
				PDF_MARGIN_TOP_PX,
				PDF_MARGIN_LEFT_PX,
				PDF_MARGIN_BOTTOM_PX,
				PDF_MARGIN_RIGHT_PX,
			],
			image: { type: 'jpeg', quality: 0.98 },
			html2canvas: {
				scale: 2,
				useCORS: true,
				allowTaint: true,
				logging: false,
				backgroundColor: '#ffffff',
				scrollX: 0,
				scrollY: 0,
				removeContainer: true,
				windowWidth: PDF_CONTENT_WIDTH_PX,
				onclone: ( clonedDoc, element ) => {
					if ( element instanceof HTMLElement ) {
						element.classList.add( PDF_EXPORT_CLASS );
						preparePageBreakTargets( element );
						finalizePdfPageLayout( element );
					}

					clonedDoc
						.querySelectorAll(
							'.alteia-single-post__sidebar-column, .alteia-single-post__back-wrap, .alteia-single-post__top-meta'
						)
						.forEach( ( node ) => {
							node.remove();
						} );

					clonedDoc
						.querySelectorAll(
							'.html2pdf__container, .html2pdf__overlay'
						)
						.forEach( ( node ) => {
							if ( node instanceof HTMLElement ) {
								node.style.overflow = 'visible';
							}
						} );
				},
			},
			jsPDF: {
				unit: 'px',
				format: [ PDF_A4_WIDTH_PX, PDF_A4_HEIGHT_PX ],
				orientation: 'portrait',
			},
			pagebreak: {
				mode: [ 'css', 'legacy' ],
				avoid: PDF_PAGE_BREAK_AVOID,
			},
		} );

		const pdfBlob = await worker.from( clone ).outputPdf( 'blob' );
		await triggerBlobDownload( pdfBlob, filename );
	} finally {
		sandbox.remove();
		cleanupHtml2PdfArtifacts();
		await waitForMemoryRelease();
	}
}

/**
 * Generate and download a PDF matching the theme print layout.
 * Downloads are queued so repeated clicks cannot overlap and exhaust memory.
 *
 * @param {HTMLElement}                                 root         Single post root element.
 * @param {string}                                      postTitle    Post title for filename.
 * @param {(state: 'generating'|'downloading') => void} [onProgress] Progress callback.
 * @return {Promise<void>}
 */
export function downloadPostPdf( root, postTitle, onProgress ) {
	const job = () => runDownloadJob( root, postTitle, onProgress );

	const queued = downloadQueue.then( job, job );
	downloadQueue = queued.catch( () => undefined );

	return queued;
}
